import { Badge, Button, Input, Label, Spinner } from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Search, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { EMAIL_REGEX, PID_REGEX, VERSION_REGEX } from '@/services/api/terms';

const IDENTIFIER_LABEL = 'Search by PID or email';
const VERSION_LABEL = 'Version (YYYY-MM-DD)';
const IDENTIFIER_ERROR = 'Enter a valid email address or PID to search';
const VERSION_ERROR = 'Enter a valid version to search';

// Empty inputs are valid — submitting them clears the corresponding active filter
const searchSchema = z.object({
  identifier: z.string().refine((value) => {
    const trimmed = value.trim();
    return trimmed === '' || EMAIL_REGEX.test(trimmed) || PID_REGEX.test(trimmed);
  }, IDENTIFIER_ERROR),
  version: z.string().refine((value) => {
    const trimmed = value.trim();
    return trimmed === '' || VERSION_REGEX.test(trimmed);
  }, VERSION_ERROR),
});

type SearchFormValues = z.infer<typeof searchSchema>;

export interface TermsSearchValue {
  identifier?: string;
  version?: string;
}

interface TermsSearchControlsProps {
  /** Version field only exists on the Terms Agreements tab */
  hasVersion: boolean;
  /** Currently applied search — rendered as closable chips */
  active: TermsSearchValue;
  onSearch: (value: TermsSearchValue) => void;
  isSearching: boolean;
  /** Unique input-id prefix per tab instance */
  idPrefix: string;
}

export const TermsSearchControls = ({ hasVersion, active, onSearch, isSearching, idPrefix }: TermsSearchControlsProps) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<SearchFormValues>({
    resolver: zodResolver(searchSchema),
    // Restore the applied search when the tab remounts
    defaultValues: { identifier: active.identifier ?? '', version: active.version ?? '' },
  });

  const submit = (values: SearchFormValues) => {
    onSearch({
      identifier: values.identifier.trim() || undefined,
      version: hasVersion ? values.version.trim() || undefined : undefined,
    });
  };

  const clearIdentifier = () => {
    setValue('identifier', '');
    onSearch({ ...active, identifier: undefined });
  };

  const clearVersion = () => {
    setValue('version', '');
    onSearch({ ...active, version: undefined });
  };

  return (
    <div className="flex flex-col gap-2">
      <form className="flex flex-wrap items-end gap-3" onSubmit={handleSubmit(submit)}>
        <div className="flex w-[25rem] max-w-full flex-col gap-1.5">
          <Label htmlFor={`${idPrefix}-identifier`}>{IDENTIFIER_LABEL}</Label>
          <Input id={`${idPrefix}-identifier`} {...register('identifier')} />
        </div>
        {hasVersion ? (
          <div className="flex w-[15rem] max-w-full flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-version`}>{VERSION_LABEL}</Label>
            <Input id={`${idPrefix}-version`} placeholder="YYYY-MM-DD" {...register('version')} />
          </div>
        ) : null}
        <Button aria-label="Search" disabled={isSearching} size="icon" type="submit" variant="outline">
          {isSearching ? <Spinner className="size-4" /> : <Search className="size-4" />}
        </Button>
      </form>
      {errors.identifier ? <p className="text-sm text-red-500">{errors.identifier.message}</p> : null}
      {hasVersion && errors.version ? <p className="text-sm text-red-500">{errors.version.message}</p> : null}
      {active.identifier || active.version ? (
        <div className="flex flex-wrap items-center gap-2">
          {active.identifier ? (
            <Badge className="gap-1">
              {active.identifier}
              <button aria-label="Clear identifier search" className="cursor-pointer" onClick={clearIdentifier} type="button">
                <X className="size-3" />
              </button>
            </Badge>
          ) : null}
          {active.version ? (
            <Badge className="gap-1">
              {active.version}
              <button aria-label="Clear version search" className="cursor-pointer" onClick={clearVersion} type="button">
                <X className="size-3" />
              </button>
            </Badge>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
TermsSearchControls.displayName = 'TermsSearchControls';
